import React, { useState, useEffect } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import api, { formatApiError, fileUrl } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import CloseRequestButton from "@/components/app/CloseRequestButton";
import OsClassification from "@/components/app/OsClassification";
import ElginCertificateActions from "@/components/app/ElginCertificateActions";
import OsEquipmentsList from "@/components/app/OsEquipmentsList";
import OsSurveySection, { SURVEY_FIELDS } from "@/components/app/OsSurveySection";
import { SCHEDULE_TIMES, STATUS_META, formatDate, AUTHORIZED_SERVICES, WARRANTY_PERIODS, PAYMENT_METHODS } from "@/lib/status";
import { toast } from "sonner";
import StaffEquipmentDialog from "@/components/app/StaffEquipmentDialog";
import QrCodeDialog from "@/components/app/QrCodeDialog";
import AssignCustomerDialog from "@/components/app/AssignCustomerDialog";
import { Printer, MessageCircle, Save, CalendarClock, FileText, Download, Mail, ShieldCheck, Award, DollarSign, QrCode, ImagePlus, Loader2, X, Wrench, Wind, UserCheck, ListChecks } from "lucide-react";

const DEFAULT_CHECKLIST = [
  "Desligar a energia do equipamento",
  "Inspeção visual do equipamento",
  "Limpeza do filtro / evaporadora",
  "Verificar gás / pressão",
  "Teste de funcionamento",
  "Limpeza do local",
  "Orientação ao cliente",
];

function waLink(phone, text) {
  let p = (phone || "").replace(/\D/g, "");
  if (p && !p.startsWith("55")) p = "55" + p;
  return `https://wa.me/${p}?text=${encodeURIComponent(text)}`;
}

export default function ServiceOrderDialog({ request, open, onOpenChange, onSaved }) {
  const { user } = useAuth();
  const [catalog, setCatalog] = useState([]);
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [arrivalDate, setArrivalDate] = useState("");
  const [arrivalTime, setArrivalTime] = useState("");
  const [departureDate, setDepartureDate] = useState("");
  const [departureTime, setDepartureTime] = useState("");
  const [travelValue, setTravelValue] = useState("");
  const [equipmentType, setEquipmentType] = useState("");
  const [osEquipments, setOsEquipments] = useState([]);
  const [sv, setSv] = useState({});
  const setSvField = (k, val) => setSv((o) => ({ ...o, [k]: val }));
  const [serviceOrigin, setServiceOrigin] = useState("");
  const [attendanceStatus, setAttendanceStatus] = useState("");
  const [performed, setPerformed] = useState("");
  const [observations, setObservations] = useState("");
  const [authService, setAuthService] = useState("");
  const [authOther, setAuthOther] = useState("");
  const [warrantyPeriod, setWarrantyPeriod] = useState("");
  const [warrantyNotes, setWarrantyNotes] = useState("");
  const [serviceValue, setServiceValue] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("");
  const [paymentConditions, setPaymentConditions] = useState("");
  const [paymentStatus, setPaymentStatus] = useState("pendente");
  const [couponInput, setCouponInput] = useState("");
  const [completionPhotos, setCompletionPhotos] = useState([]);
  const [partsUsed, setPartsUsed] = useState([]);
  const [quoteItems, setQuoteItems] = useState([]);
  const [checklist, setChecklist] = useState([]);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [busy, setBusy] = useState(false);
  const [emailing, setEmailing] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [laudoBusy, setLaudoBusy] = useState(false);
  const [laudoEmailing, setLaudoEmailing] = useState(false);
  const [showEquipReg, setShowEquipReg] = useState(false);
  const [qrEquip, setQrEquip] = useState(null);
  const [showAssignCustomer, setShowAssignCustomer] = useState(false);

  useEffect(() => {
    if (open && request) {
      setDate(request.scheduled_date || "");
      setTime(request.scheduled_time || "");
      setArrivalDate(request.arrival_date || "");
      setArrivalTime(request.arrival_time || "");
      setDepartureDate(request.departure_date || "");
      setDepartureTime(request.departure_time || "");
      setTravelValue(request.travel_value != null ? String(request.travel_value) : "");
      setEquipmentType(request.equipment_type || "");
      const mainEq = (request.brand || request.model || request.serial_number || request.equipment_type)
        ? [{ equipment_type: request.equipment_type || "", brand: request.brand || "", model: request.model || "",
          serial_number: request.serial_number || "", capacity_btu: request.capacity_btu || "", voltage: request.voltage || "",
          location: request.equipment_location || "", photo: request.equipment_photo || "" }] : [];
      setOsEquipments(request.os_equipments?.length ? request.os_equipments : mainEq);
      const tz = { timeZone: "America/Sao_Paulo" };
      const cAt = request.concluded_at ? new Date(request.concluded_at) : null;
      const svInit = {
        serviceType: request.service_type || "", budget: request.budget_approval || "",
        firstVisitDate: request.first_visit_date || "", firstVisitTime: request.first_visit_time || "",
        concludedDate: cAt ? cAt.toLocaleDateString("sv-SE", tz) : "",
        concludedTime: cAt ? cAt.toLocaleTimeString("pt-BR", { ...tz, hour: "2-digit", minute: "2-digit" }) : "",
        survey: request.survey || {},
      };
      setSv({ ...svInit, _init: svInit });
      setServiceOrigin(request.service_origin || "");
      setAttendanceStatus(request.attendance_status || "");
      setPerformed(request.service_performed || "");
      setObservations(request.observations || "");
      setAuthService(request.authorized_service || "");
      setAuthOther(request.authorized_service_other || "");
      setWarrantyPeriod(request.warranty_period || "");
      setWarrantyNotes(request.warranty_notes || "");
      setServiceValue(request.service_value != null ? String(request.service_value) : "");
      setPaymentMethod(request.payment_method || "");
      setPaymentConditions(request.payment_conditions || "");
      setPaymentStatus(request.payment_status || "pendente");
      setCouponInput("");
      setCompletionPhotos((request.completion_photos || []).map((p) => ({ path: p })));
      setPartsUsed((request.parts_used || []).map((p) => ({ name: p.name, quantity: p.quantity })));
      setQuoteItems((request.quote_items || []).map((q) => ({ description: q.description, kind: q.kind || "peca", quantity: q.quantity ?? 1, unit_price: q.unit_price ?? 0 })));
      setChecklist((request.service_checklist && request.service_checklist.length)
        ? request.service_checklist.map((i) => ({ label: i.label, done: !!i.done }))
        : DEFAULT_CHECKLIST.map((l) => ({ label: l, done: false })));
    }
  }, [open, request]);

  useEffect(() => {
    if (open && ["manager", "franchisee", "technician"].includes(user?.role)) {
      api.get("/services").then(({ data }) => setCatalog(data)).catch(() => setCatalog([]));
    }
  }, [open, user]);

  if (!request) return null;
  const isClosed = ["concluido", "cancelado"].includes(request.status);
  const canEditClosed = ["manager", "franchisee"].includes(user?.role);
  const autoOrigin = (request?.is_warranty || request?.service_type === "Garantia") ? "garantia"
    : request?.service_type === "Instalação" ? "instalacao"
    : (request?.contract_id || request?.customer_is_contract) ? "contrato" : "particular";
  const finished = isClosed && !canEditClosed;
  const techAfter = user?.role === "technician" && request?.status === "concluido";
  const saveEquipmentsOnly = async () => {
    try {
      await api.patch(`/requests/${request.id}/service-order`, {
        os_equipments: osEquipments.filter((e) => Object.values(e).some(Boolean)),
      });
      toast.success("Equipamentos salvos na OS!");
      onSaved?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };
  const osNumber = request.ticket_number
    ? `OS-${String(request.ticket_number).padStart(5, "0")}`
    : `OS-${(request.id || "").slice(0, 8).toUpperCase()}`;
  const statusLabel = (STATUS_META[request.status] || {}).label || request.status;
  const address = request.address_same_as_registered ? "Endereço de cadastro do cliente" : (request.service_address || "-");
  const schedText = date ? `${new Date(date + "T00:00:00").toLocaleDateString("pt-BR")}${time ? ` às ${time}` : ""}` : "A combinar";
  const authText = authService === "OUTROS" ? `Outros${authOther ? ` — ${authOther}` : ""}` : authService;
  const executedBy = (() => {
    let name = request.franchisee_name || request.franchisee_razao_social;
    if (!name) return null;
    const razao = (request.franchisee_razao_social || "").trim();
    if (razao && razao !== name) name = `${name} (${razao})`;
    return request.franchisee_cnpj ? `${name} — CNPJ ${request.franchisee_cnpj}` : name;
  })();
  const isQuote = !!request.is_quote;
  const quoteTotal = quoteItems.reduce((s, it) => s + (Number(it.quantity) || 0) * (Number(it.unit_price) || 0), 0);
  const effectiveValue = isQuote ? quoteTotal : (serviceValue !== "" ? Number(serviceValue) : NaN);
  const valueText = !isNaN(effectiveValue) && effectiveValue !== ""
    ? "R$ " + Number(effectiveValue).toFixed(2).replace(".", ",") : "";
  const isPix = paymentMethod === "Pix";
  const pixKey = request.payment_pix_key;
  const pixQr = request.payment_pix_qr;
  const pixOwner = request.payment_pix_owner;

  const handleCompletionPhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    if (completionPhotos.length + files.length > 3) { toast.error("Máximo de 3 fotos."); return; }
    setUploadingPhoto(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setCompletionPhotos((p) => [...p, { path: data.path }]);
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Falha ao enviar imagem");
    } finally {
      setUploadingPhoto(false);
    }
  };

  const save = async (silent = false) => {
    setBusy(true);
    try {
      await api.patch(`/requests/${request.id}/service-order`, {
        scheduled_date: date || null, scheduled_time: time || null, service_performed: performed || null,
        arrival_date: arrivalDate || null, arrival_time: arrivalTime || null,
        departure_date: departureDate || null, departure_time: departureTime || null,
        travel_value: travelValue !== "" ? Number(travelValue) : null,
        equipment_type: equipmentType || null,
        service_type: sv.serviceType || null,
        budget_approval: sv.budget || "",
        first_visit_date: sv.firstVisitDate || null, first_visit_time: sv.firstVisitTime || null,
        survey: sv.survey || {},
        ...(canEditClosed && sv.concludedDate && (sv.concludedDate !== sv._init?.concludedDate || sv.concludedTime !== sv._init?.concludedTime)
          ? { concluded_date: sv.concludedDate, concluded_time: sv.concludedTime || null } : {}),
        os_equipments: osEquipments.filter((e) => Object.values(e).some(Boolean)),
        service_origin: serviceOrigin || null, attendance_status: attendanceStatus || null,
        observations: observations || null,
        authorized_service: authService || null,
        authorized_service_other: authService === "OUTROS" ? (authOther || null) : null,
        warranty_period: warrantyPeriod || null,
        warranty_notes: warrantyNotes || null,
        service_value: isQuote ? undefined : (serviceValue !== "" ? Number(serviceValue) : null),
        quote_items: isQuote
          ? quoteItems.filter((it) => it.description && it.description.trim())
              .map((it) => ({ description: it.description.trim(), kind: it.kind || "peca", quantity: Number(it.quantity) || 1, unit_price: Number(it.unit_price) || 0 }))
          : undefined,
        payment_method: paymentMethod || null,
        payment_conditions: paymentConditions || null,
        payment_status: paymentStatus || null,
        coupon_code: (!request.coupon_code && couponInput.trim()) ? couponInput.trim().toUpperCase() : undefined,
        completion_photos: completionPhotos.map((p) => p.path),
        parts_used: partsUsed.filter((p) => p.name && p.name.trim()).map((p) => ({ name: p.name.trim(), quantity: Number(p.quantity) || 1 })),
        service_checklist: checklist.map((c) => ({ label: c.label, done: !!c.done })),
      });
      if (!silent) { toast.success("Ordem de serviço salva!"); onSaved?.(); }
      return true;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
      return false;
    } finally {
      setBusy(false);
    }
  };

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const { data } = await api.get(`/requests/${request.id}/service-order/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${osNumber}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Falha ao gerar o PDF.");
    } finally {
      setDownloading(false);
    }
  };

  const sendEmail = async () => {
    setEmailing(true);
    try {
      const { data } = await api.post(`/requests/${request.id}/service-order/email`);
      toast.success(`OS enviada por e-mail para ${data.email}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Falha ao enviar o e-mail.");
    } finally {
      setEmailing(false);
    }
  };

  const downloadLaudo = async () => {
    setLaudoBusy(true);
    try {
      const { data } = await api.get(`/requests/${request.id}/laudo/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `Laudo-${osNumber}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Falha ao gerar o laudo.");
    } finally {
      setLaudoBusy(false);
    }
  };

  const sendLaudoEmail = async () => {
    setLaudoEmailing(true);
    try {
      const { data } = await api.post(`/requests/${request.id}/laudo/email`);
      toast.success(`Laudo enviado por e-mail para ${data.email}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Falha ao enviar o e-mail.");
    } finally {
      setLaudoEmailing(false);
    }
  };

  const summaryText = () => {
    const lines = [
      `*Clima Branca — Ordem de Serviço ${osNumber}*`,
      `Cliente: ${request.customer_name || "-"}`,
      request.equipment_label || request.equipment ? `Equipamento: ${request.equipment_label || request.equipment}` : null,
      `Serviço: ${request.service_type || "-"}`,
      authText ? `Serviço autorizado: ${authText}` : null,
      `Endereço: ${address}`,
      `Agendamento: ${schedText}`,
      request.assigned_technician_name ? `Técnico: ${request.assigned_technician_name}` : null,
      executedBy ? `Serviço executado por: ${executedBy}` : null,
      request.defect_description ? `Descrição: ${request.defect_description}` : null,
      observations ? `Observações: ${observations}` : null,
      valueText ? `Valor do serviço: ${valueText}` : null,
      paymentMethod ? `Forma de pagamento: ${paymentMethod}` : null,
      paymentConditions ? `Condições: ${paymentConditions}` : null,
      isPix && pixKey ? `Chave Pix: ${pixKey}` : null,
      `Status: ${statusLabel}`,
    ].filter(Boolean);
    return lines.join("\n");
  };

  const sendWhatsApp = () => {
    const phone = request.customer_phone || request.service_responsible_phone;
    if (!phone) { toast.error("Chamado sem telefone do cliente."); return; }
    window.open(waLink(phone, summaryText()), "_blank");
  };

  const printOS = () => {
    const logo = `${window.location.origin}/logo-clima-branca-icon.png`;
    const esc = (s) => String(s == null ? "" : s).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
    const chk = (c) => (c ? "&#9745;" : "&#9744;"); // ☑ / ☐
    const allEqs = osEquipments.filter((e) => Object.entries(e).some(([k, v]) => k !== "photo" && v));
    const e0 = allEqs[0] || {};
    const gq = (k) => e0[k] || request[k];
    const eqType = e0.equipment_type || equipmentType || request.equipment_type;
    const extraEqs = allEqs.length > 1 ? allEqs : [];
    const svy = sv.survey || {};
    const hasSurvey = SURVEY_FIELDS.some(([k]) => svy[k]) || (svy.photos || []).length || sv.budget;
    const fmtD = (d) => (d ? new Date(d + "T00:00:00").toLocaleDateString("pt-BR") : "");
    const eqStr = (eqType || request.equipment_label || request.equipment || "").toLowerCase();
    const isSplit = eqStr.includes("split") && !eqStr.includes("piso");
    const isPisoTeto = eqStr.includes("piso");
    const isCassete = eqStr.includes("cassete") || eqStr.includes("k7");
    const isVrf = eqStr.includes("vrf") || eqStr.includes("vrv");
    const isRefrig = eqStr.includes("refriger") || eqStr.includes("geladeira") || eqStr.includes("freezer");
    const isOutroEq = !(isSplit || isPisoTeto || isCassete || isVrf || isRefrig);
    const org = serviceOrigin || autoOrigin;
    const st = attendanceStatus || (["concluido", "cancelado"].includes(request.status) ? request.status : "");
    const maoObra = quoteItems.filter((it) => it.kind === "mao_de_obra").reduce((s, it) => s + (Number(it.quantity) || 0) * (Number(it.unit_price) || 0), 0);
    const pecasVal = quoteItems.filter((it) => it.kind === "peca").reduce((s, it) => s + (Number(it.quantity) || 0) * (Number(it.unit_price) || 0), 0);
    const brl = (v) => "R$ " + Number(v || 0).toFixed(2).replace(".", ",");
    const travelVal = Number(travelValue) || 0;
    const totalVal = isQuote ? quoteTotal + travelVal
      : (!isNaN(effectiveValue) && effectiveValue !== "" ? Number(effectiveValue) : (maoObra + pecasVal + travelVal));
    const partsRows = (quoteItems.length ? quoteItems : partsUsed.map((p) => ({ description: p.name, quantity: p.quantity, unit_price: "" })))
      .filter((it) => (it.description || "").trim())
      .map((it) => `<tr><td></td><td>${esc(it.description)}</td><td style="text-align:center">${esc(it.quantity ?? "")}</td><td style="text-align:right">${it.unit_price === "" || it.unit_price == null ? "" : brl(Number(it.quantity || 1) * Number(it.unit_price || 0))}</td></tr>`)
      .join("") || `<tr><td>&nbsp;</td><td></td><td></td><td></td></tr><tr><td>&nbsp;</td><td></td><td></td><td></td></tr>`;
    const line = (v) => `<span class="fill">${esc(v) || "&nbsp;"}</span>`;
    const frA = request.franchisee_address || {};
    const box = (v) => `<div class="box">${esc(v) || "&nbsp;"}</div>`;
    const win = window.open("", "_blank", "width=820,height=1000");
    if (!win) { toast.error("Permita pop-ups para ver a prévia."); return; }
    win.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>${esc(osNumber)} · Prévia</title>
      <style>
        *{box-sizing:border-box;margin:0;padding:0;font-family:Arial,Helvetica,sans-serif;color:#0f172a}
        body{padding:24px;font-size:12px;line-height:1.35}
        .head{border:2px solid #0a2a6c;border-radius:10px;overflow:hidden;margin-bottom:12px}
        .head .top{display:flex;align-items:center;gap:12px;background:linear-gradient(135deg,#0a2a6c,#1e7be0);color:#fff;padding:12px 16px}
        .head .top img{height:46px;width:46px;border-radius:8px;background:#fff;padding:3px}
        .head .top b{font-size:18px;display:block;letter-spacing:.5px}
        .head .top span{font-size:10px;opacity:.9;text-transform:uppercase;letter-spacing:2px}
        .head .os{margin-left:auto;text-align:right}
        .head .os .t{font-size:15px;font-weight:bold}
        .head .os .n{font-size:11px}
        .head .auth{background:#eef4ff;padding:6px 16px;text-align:center;font-size:10px;color:#0a2a6c;font-weight:bold;letter-spacing:.5px}
        .head .auth .brands{font-size:11px;letter-spacing:2px;margin-top:2px}
        h2{background:#0a2a6c;color:#fff;font-size:11px;text-transform:uppercase;letter-spacing:1px;padding:5px 10px;border-radius:5px;margin:14px 0 8px}
        table{width:100%;border-collapse:collapse}
        .grid td{padding:3px 6px;vertical-align:top}
        .grid td.k{color:#475569;font-weight:bold;white-space:nowrap;width:150px}
        .fill{display:inline-block;border-bottom:1px solid #94a3b8;min-width:60%;padding:0 4px}
        .opts{display:flex;flex-wrap:wrap;gap:12px;font-size:11px;padding:2px 6px}
        .box{border:1px solid #cbd5e1;border-radius:6px;min-height:44px;padding:6px 8px;white-space:pre-wrap}
        .parts{border:1px solid #cbd5e1;border-radius:6px;overflow:hidden;margin-top:4px}
        .parts th{background:#f1f5f9;font-size:10px;text-transform:uppercase;letter-spacing:.5px;color:#475569;padding:5px 8px;text-align:left;border-bottom:1px solid #e2e8f0}
        .parts td{padding:5px 8px;border-bottom:1px solid #eef2f7;font-size:11px}
        .vals{width:280px;margin-left:auto}
        .vals td{padding:4px 8px;font-size:12px}
        .vals td.k{color:#475569}
        .vals .tot td{border-top:2px solid #0a2a6c;font-weight:bold;font-size:14px;color:#0a2a6c}
        .sign{display:flex;gap:28px;margin-top:16px}
        .sign div{flex:1;text-align:center;border-top:1px solid #334155;padding-top:6px;font-size:11px;color:#334155}
        .accept{border:1px dashed #94a3b8;border-radius:6px;padding:10px;font-size:11px;color:#334155;margin-top:6px}
        .foot{margin-top:22px;border-top:2px solid #0a2a6c;padding-top:8px;text-align:center;font-size:10px;color:#64748b}
        .foot b{color:#0a2a6c;font-size:11px}
        .preview-flag{position:fixed;top:6px;right:6px;background:#f59e0b;color:#fff;font-size:9px;font-weight:bold;padding:3px 8px;border-radius:10px;letter-spacing:1px}
        @media print{.preview-flag{display:none}}
      </style></head>
      <body onload="setTimeout(function(){window.print();},350)">
        <div class="head">
          <div class="top">
            <img src="${logo}"/>
            <div><b>CLIMA BRANCA FRANQUIAS</b><span>Soluções em Climatização e Refrigeração</span></div>
            <div class="os"><div class="t">ORDEM DE SERVIÇO</div><div class="n">Nº ${esc(osNumber)} &nbsp;|&nbsp; Data: ${new Date().toLocaleDateString("pt-BR")}</div></div>
          </div>
          <div class="auth">ASSISTÊNCIA TÉCNICA AUTORIZADA / CREDENCIADA<div class="brands">ELGIN • HQ • AGRATTO • AIWA • MUNDIAL • SYMPHONY</div></div>
        </div>

        <h2>Dados do Franqueado</h2>
        <table class="grid">
          <tr><td class="k">Razão Social</td><td>${line(request.franchisee_razao_social)}</td></tr>
          <tr><td class="k">Nome Fantasia</td><td>${line(request.franchisee_name)}</td></tr>
          <tr><td class="k">CNPJ</td><td>${line(request.franchisee_cnpj)}</td></tr>
          <tr><td class="k">Endereço</td><td>${line([frA.street, frA.number, frA.complement, frA.neighborhood].filter(Boolean).join(", "))}</td></tr>
          <tr><td class="k">Cidade/UF &middot; CEP</td><td>${line([[frA.city, frA.state].filter(Boolean).join(" / "), frA.cep || request.franchisee_base_cep].filter(Boolean).join(" · "))}</td></tr>
          <tr><td class="k">Telefone/WhatsApp</td><td>${line(request.franchisee_phone)}</td></tr>
          <tr><td class="k">E-mail</td><td>${line(request.franchisee_email)}</td></tr>
        </table>

        <h2>Dados do Cliente</h2>
        <table class="grid">
          <tr><td class="k">Nome/Razão Social</td><td>${line(request.customer_name)}</td></tr>
          <tr><td class="k">CPF/CNPJ</td><td>${line(request.customer_document)}</td></tr>
          <tr><td class="k">Telefone/WhatsApp</td><td>${line(request.customer_phone)}</td></tr>
          <tr><td class="k">E-mail</td><td>${line(request.customer_email)}</td></tr>
          <tr><td class="k">Endereço do atendimento</td><td>${line(address)}</td></tr>
        </table>

        <h2>Equipamento</h2>
        <div class="opts">
          <span>${chk(isSplit)} Split${isSplit && eqType ? ` (${esc(eqType)})` : ""}</span><span>${chk(isPisoTeto)} Piso-Teto</span><span>${chk(isCassete)} Cassete</span>
          <span>${chk(isVrf)} VRF</span><span>${chk(isRefrig)} Refrigerador</span><span>${chk(isOutroEq)} Outro</span>
        </div>
        <table class="grid">
          <tr><td class="k">Fabricante</td><td>${line(gq("brand") || authText)}</td><td class="k">Modelo</td><td>${line(gq("model"))}</td></tr>
          <tr><td class="k">Nº de Série</td><td>${line(gq("serial_number"))}</td><td class="k">Capacidade</td><td>${line(gq("capacity_btu"))}</td></tr>
          <tr><td class="k">Tensão</td><td>${line(gq("voltage"))}</td><td class="k">Equipamento</td><td>${line(request.equipment_label || request.equipment)}</td></tr>
        </table>
        ${extraEqs.length ? `<div style="margin-top:6px;font-weight:bold;color:#475569">Equipamentos desta OS (${extraEqs.length})</div>
        <table class="parts" style="width:100%;border-collapse:collapse"><tr><th>#</th><th>Tipo</th><th>Fabricante</th><th>Modelo</th><th>Nº de Série</th><th>Capacidade</th><th>Tensão</th><th>Local</th></tr>
        ${extraEqs.map((e, i) => `<tr><td>${i + 1}</td><td>${esc(e.equipment_type)}</td><td>${esc(e.brand)}</td><td>${esc(e.model)}</td><td>${esc(e.serial_number)}</td><td>${esc(e.capacity_btu)}</td><td>${esc(e.voltage)}</td><td>${esc(e.location)}</td></tr>`).join("")}
        </table>` : ""}
        <div class="opts" style="margin-top:6px">
          <b style="color:#475569">Origem do chamado:</b>
          <span>${chk(org === "garantia")} Garantia fabricante</span><span>${chk(org === "contrato")} Contrato de manutenção</span>
          <span>${chk(org === "particular")} Particular</span><span>${chk(org === "instalacao")} Instalação</span><span>${chk(org === "retorno")} Retorno</span>
        </div>
        <div style="margin-top:4px"><b style="color:#475569">Tipo de serviço:</b> ${esc(sv.serviceType || request.service_type)}</div>

        <h2>Defeito informado pelo cliente</h2>
        ${box(request.defect_description)}
        <h2>Diagnóstico técnico</h2>
        ${box(observations)}
        <h2>Serviço realizado</h2>
        ${box(performed)}

        ${hasSurvey ? `<h2>Laudo da vistoria / levantamento (1ª visita)</h2>
        <div class="opts"><b style="color:#475569">Orçamento:</b><span>${chk(sv.budget === "aprovado")} Aprovado</span><span>${chk(sv.budget === "nao_aprovado")} Não aprovado</span></div>
        <table class="grid">${SURVEY_FIELDS.filter(([k]) => svy[k]).map(([k, l]) => `<tr><td class="k">${esc(l)}</td><td>${esc(svy[k])}</td></tr>`).join("")}</table>
        ${(svy.photos || []).length ? `<div style="display:flex;gap:6px;margin-top:6px">${svy.photos.slice(0, 4).map((p) => `<img src="${esc(fileUrl(p))}" style="width:120px;height:120px;object-fit:cover;border-radius:6px"/>`).join("")}</div>` : ""}` : ""}

        <h2>Peças / Materiais utilizados</h2>
        <table class="parts">
          <tr><th style="width:80px">Código</th><th>Descrição</th><th style="width:50px;text-align:center">Qtd.</th><th style="width:90px;text-align:right">Valor</th></tr>
          ${partsRows}
        </table>

        <h2>Status do atendimento</h2>
        <div class="opts">
          <span>${chk(st === "concluido")} Concluído</span><span>${chk(st === "aguardando_peca")} Aguardando peça</span>
          <span>${chk(st === "aguardando_aprovacao")} Aguardando aprovação</span><span>${chk(st === "retorno_tecnico")} Retorno técnico</span>
          <span>${chk(st === "sem_reparo")} Equipamento sem reparo</span><span>${chk(st === "cancelado")} Cancelado</span>
        </div>

        <h2>Registro técnico</h2>
        <table class="grid">
          <tr><td class="k">Técnico responsável</td><td>${line(request.assigned_technician_name)}</td></tr>
          <tr><td class="k">Data real da 1ª visita</td><td>${line(fmtD(sv.firstVisitDate))}</td><td class="k">Hora</td><td>${line(sv.firstVisitTime)}</td></tr>
          <tr><td class="k">Data de conclusão</td><td>${line(fmtD(sv.concludedDate))}</td><td class="k">Hora</td><td>${line(sv.concludedDate ? sv.concludedTime : "")}</td></tr>
          <tr><td class="k">Data de chegada</td><td>${line(arrivalDate ? new Date(arrivalDate + "T00:00:00").toLocaleDateString("pt-BR") : "")}</td><td class="k">Hora</td><td>${line(arrivalTime)}</td></tr>
          <tr><td class="k">Data de saída</td><td>${line(departureDate ? new Date(departureDate + "T00:00:00").toLocaleDateString("pt-BR") : "")}</td><td class="k">Hora</td><td>${line(departureTime)}</td></tr>
          <tr><td class="k">Fotos do serviço</td><td>${chk(completionPhotos.length > 0)} Antes &nbsp; ${chk(completionPhotos.length > 1)} Durante &nbsp; ${chk(completionPhotos.length > 2)} Depois</td></tr>
        </table>

        <h2>Valores</h2>
        <table class="vals">
          <tr><td class="k">Mão de obra</td><td style="text-align:right">${maoObra ? brl(maoObra) : "R$ ________"}</td></tr>
          <tr><td class="k">Peças</td><td style="text-align:right">${pecasVal ? brl(pecasVal) : "R$ ________"}</td></tr>
          <tr><td class="k">Deslocamento</td><td style="text-align:right">${travelVal ? brl(travelVal) : "R$ ________"}</td></tr>
          <tr class="tot"><td>TOTAL</td><td style="text-align:right">${brl(totalVal)}</td></tr>
        </table>

        <h2>Aceite do cliente</h2>
        <div class="accept">Declaro que fui informado sobre o diagnóstico, serviços executados, peças utilizadas e condições do atendimento registrados nesta Ordem de Serviço.</div>
        <div class="sign">
          <div>Cliente/Responsável${request.responsible_name || request.customer_name ? `<br/><small>${esc(request.responsible_name || request.customer_name)}</small>` : ""}</div>
          <div>Assinatura</div>
          <div>Técnico${request.assigned_technician_name ? `<br/><small>${esc(request.assigned_technician_name)}</small>` : ""}</div>
        </div>

        <div class="foot">
          <b>CLIMA BRANCA — SOLUÇÕES EM CLIMATIZAÇÃO E REFRIGERAÇÃO</b><br/>
          WhatsApp: (21) 4063-9851 &nbsp;&middot;&nbsp; climabranca.com.br<br/>
          Serviço Autorizado / Credenciado: ELGIN | HQ | AGRATTO | AIWA | MUNDIAL | SYMPHONY
        </div>
      </body></html>`);
    win.document.close();
  };


  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="service-order-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><FileText className="h-5 w-5 text-[#1e7be0]" /> Ordem de Serviço · {osNumber}</DialogTitle>
          <DialogDescription>{request.customer_name} — {request.service_type}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="rounded-lg bg-slate-50 p-3 text-sm space-y-1">
            {(request.equipment_label || request.equipment) && <p><b>Equipamento:</b> {request.equipment_label || request.equipment}</p>}
            {request.equipment_location && <p data-testid="os-service-location"><b>Local / Sala:</b> {request.equipment_location}</p>}
            <p><b>Endereço:</b> {address}</p>
            {request.assigned_technician_name && <p><b>Técnico:</b> {request.assigned_technician_name}</p>}
            {executedBy && <p data-testid="os-executed-by"><b>Serviço executado por:</b> {executedBy}</p>}
            <p><b>Status:</b> {statusLabel}</p>
          </div>

          <div className="rounded-lg border border-blue-200 bg-blue-50/40 p-3 space-y-2" data-testid="os-equipment-qr-section">
            <Label className="text-sm flex items-center gap-1.5 text-[#0a2a6c]"><QrCode className="h-4 w-4" /> Equipamento + QR Code</Label>
            {request.register_equipment_in_field && !request.equipment_id && (
              <div className="flex items-start gap-2 rounded-md bg-amber-50 border border-amber-200 p-2 text-xs text-amber-800" data-testid="os-field-register-note">
                <UserCheck className="h-4 w-4 mt-0.5 flex-shrink-0" />
                Este chamado foi aberto para cadastrar o equipamento em campo. Cadastre o aparelho abaixo durante o atendimento.
              </div>
            )}
            <p className="text-xs text-slate-500">
              Cadastre o aparelho deste atendimento e gere um QR Code com o histórico de serviços (instalação, conserto, manutenção).
            </p>
            <Button type="button" variant="outline" className="gap-1.5 border-[#1e7be0] text-[#1e7be0] hover:bg-blue-50 w-full"
              onClick={() => setShowEquipReg(true)} data-testid="os-register-equipment-button">
              <Wind className="h-4 w-4" /> Cadastrar equipamento + gerar QR
            </Button>
          </div>

          {!request.customer_id && (
            <div className="rounded-lg border border-amber-200 bg-amber-50/40 p-3 space-y-2" data-testid="os-assign-customer-section">
              <Label className="text-sm flex items-center gap-1.5 text-amber-800"><UserCheck className="h-4 w-4" /> Cliente não cadastrado</Label>
              <p className="text-xs text-slate-500">
                Este chamado não está associado a um cliente cadastrado. Associe a um cliente do sistema (para histórico, fidelidade e login).
              </p>
              <Button type="button" variant="outline" className="gap-1.5 border-amber-400 text-amber-700 hover:bg-amber-100 w-full"
                onClick={() => setShowAssignCustomer(true)} data-testid="os-assign-customer-button">
                <UserCheck className="h-4 w-4" /> Associar a cliente cadastrado
              </Button>
            </div>
          )}

          <div className="rounded-lg border border-slate-200 p-3 space-y-2">
            <Label className="text-sm flex items-center gap-1.5"><CalendarClock className="h-4 w-4 text-[#1e7be0]" /> Agendamento (08h–18h)</Label>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1.5">
                <Label className="text-xs">Data</Label>
                <Input type="date" value={date} disabled={finished} onChange={(e) => setDate(e.target.value)} data-testid="os-date-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Horário</Label>
                <Select value={time || "none"} disabled={finished} onValueChange={(v) => setTime(v === "none" ? "" : v)}>
                  <SelectTrigger data-testid="os-time-select"><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sem horário</SelectItem>
                    {SCHEDULE_TIMES.map((t) => <SelectItem key={t} value={t} data-testid={`os-time-${t}`}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-slate-200 p-3 space-y-2">
            <Label className="text-sm flex items-center gap-1.5"><CalendarClock className="h-4 w-4 text-[#1e7be0]" /> Registro técnico — chegada e saída</Label>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1.5">
                <Label className="text-xs">Chegada — data</Label>
                <Input type="date" value={arrivalDate} disabled={finished} onChange={(e) => setArrivalDate(e.target.value)} data-testid="os-arrival-date-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Chegada — hora</Label>
                <Input type="time" value={arrivalTime} disabled={finished} onChange={(e) => setArrivalTime(e.target.value)} data-testid="os-arrival-time-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Saída — data</Label>
                <Input type="date" value={departureDate} disabled={finished} onChange={(e) => setDepartureDate(e.target.value)} data-testid="os-departure-date-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Saída — hora</Label>
                <Input type="time" value={departureTime} disabled={finished} onChange={(e) => setDepartureTime(e.target.value)} data-testid="os-departure-time-input" />
              </div>
            </div>
          </div>

          <OsClassification origin={serviceOrigin || autoOrigin} onOrigin={setServiceOrigin}
            status={attendanceStatus} onStatus={setAttendanceStatus}
            travel={travelValue} onTravel={setTravelValue} disabled={finished}
            />

          <OsSurveySection v={sv} set={setSvField} disabled={finished} canEditDates={canEditClosed} />

          <OsEquipmentsList items={osEquipments} onChange={setOsEquipments}
            disabled={finished && !techAfter}
            onSaveOnly={finished && techAfter ? saveEquipmentsOnly : null} />

          <div className="rounded-lg border border-slate-200 p-3 space-y-2">
            <Label className="text-sm flex items-center gap-1.5"><ShieldCheck className="h-4 w-4 text-[#1e7be0]" /> Serviço autorizado</Label>
            <Select value={authService || "none"} disabled={finished} onValueChange={(v) => setAuthService(v === "none" ? "" : v)}>
              <SelectTrigger data-testid="os-authorized-select"><SelectValue placeholder="Selecione" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Não informado</SelectItem>
                {AUTHORIZED_SERVICES.map((s) => <SelectItem key={s} value={s} data-testid={`os-authorized-${s}`}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
            {authService === "OUTROS" && (
              <Input value={authOther} disabled={finished} onChange={(e) => setAuthOther(e.target.value)}
                placeholder="Informe o serviço autorizado" data-testid="os-authorized-other-input" />
            )}
          </div>

          <div className="rounded-lg border border-slate-200 p-3 space-y-2">
            <Label className="text-sm flex items-center gap-1.5"><Award className="h-4 w-4 text-[#1e7be0]" /> Garantia do serviço</Label>
            <div className="space-y-1.5">
              <Label className="text-xs">Tempo de garantia</Label>
              <Select value={warrantyPeriod || "none"} disabled={finished} onValueChange={(v) => setWarrantyPeriod(v === "none" ? "" : v)}>
                <SelectTrigger data-testid="os-warranty-select"><SelectValue placeholder="Selecione" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Não informado</SelectItem>
                  {WARRANTY_PERIODS.map((w) => <SelectItem key={w} value={w} data-testid={`os-warranty-${w}`}>{w}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Observações da garantia</Label>
              <Textarea rows={2} value={warrantyNotes} disabled={finished} onChange={(e) => setWarrantyNotes(e.target.value)}
                placeholder="Condições e cobertura da garantia" data-testid="os-warranty-notes-input" />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Observações do atendimento</Label>
            <Textarea rows={3} value={observations} disabled={finished} onChange={(e) => setObservations(e.target.value)}
              placeholder="Detalhes da instalação, conserto ou visita técnica" data-testid="os-observations-input" />
          </div>

          <div className="space-y-1.5">
            <Label>Serviço executado / Observações</Label>
            <Textarea rows={4} value={performed} disabled={finished} onChange={(e) => setPerformed(e.target.value)}
              placeholder="Descreva o serviço realizado, peças trocadas, recomendações..." data-testid="os-performed-input" />
          </div>

          <div className="rounded-lg border border-slate-200 p-3 space-y-2" data-testid="os-checklist-section">
            <div className="flex items-center justify-between">
              <Label className="text-sm flex items-center gap-1.5"><ListChecks className="h-4 w-4 text-[#1e7be0]" /> Check-list do serviço</Label>
              <span className="text-xs font-medium text-slate-500" data-testid="os-checklist-progress">
                {checklist.filter((c) => c.done).length}/{checklist.length} concluídas
              </span>
            </div>
            <div className="space-y-0.5">
              {checklist.map((it, i) => (
                <label key={i} className="flex items-center gap-2.5 py-1 cursor-pointer" data-testid={`os-checklist-item-${i}`}>
                  <Checkbox checked={it.done} disabled={finished}
                    onCheckedChange={(v) => setChecklist((arr) => arr.map((x, j) => j === i ? { ...x, done: !!v } : x))}
                    data-testid={`os-checklist-check-${i}`} />
                  <span className={`text-sm ${it.done ? "text-slate-400 line-through" : "text-slate-700"}`}>{it.label}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="flex items-center gap-1.5"><ImagePlus className="h-4 w-4 text-[#1e7be0]" /> Fotos da conclusão (até 3)</Label>
            <p className="text-[11px] text-slate-500" data-testid="os-closing-photo-hint">Obrigatório para encerrar: foto do equipamento (aqui ou no cadastro do aparelho).</p>
            <div className="flex gap-2 flex-wrap">
              {completionPhotos.map((p, i) => (
                <div key={i} className="relative h-16 w-16">
                  <img src={fileUrl(p.path)} alt="conclusão" className="h-16 w-16 object-cover rounded-md border border-slate-200" />
                  {!finished && (
                    <button type="button" onClick={() => setCompletionPhotos((arr) => arr.filter((_, j) => j !== i))}
                      className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full p-0.5" data-testid={`os-remove-photo-${i}`}>
                      <X className="h-3 w-3" />
                    </button>
                  )}
                </div>
              ))}
              {!finished && completionPhotos.length < 3 && (
                <label className="h-16 w-16 rounded-md border-2 border-dashed border-slate-300 flex items-center justify-center cursor-pointer hover:border-blue-400" data-testid="os-photo-upload-label">
                  {uploadingPhoto ? <Loader2 className="h-5 w-5 text-slate-400 animate-spin" /> : <ImagePlus className="h-5 w-5 text-slate-400" />}
                  <input type="file" accept="image/png,image/jpeg,image/webp" multiple className="hidden" onChange={handleCompletionPhotos} data-testid="os-photo-input" />
                </label>
              )}
            </div>
          </div>

          {isQuote && (
            <div className="rounded-lg border border-amber-200 bg-amber-50/40 p-3 space-y-2" data-testid="quote-items-editor">
              <Label className="text-sm flex items-center gap-1.5 text-amber-800"><FileText className="h-4 w-4" /> Orçamento detalhado (mão de obra + peças)</Label>
              {quoteItems.length === 0 && <p className="text-xs text-slate-400">Adicione os itens que compõem o orçamento. O total é calculado automaticamente.</p>}
              {quoteItems.map((it, i) => {
                const upd = (k, v) => setQuoteItems((arr) => arr.map((x, idx) => idx === i ? { ...x, [k]: v } : x));
                const sub = (Number(it.quantity) || 0) * (Number(it.unit_price) || 0);
                return (
                  <div key={i} className="grid grid-cols-12 gap-1.5 items-center" data-testid={`quote-item-row-${i}`}>
                    <Input className="col-span-5" placeholder="Descrição (ex: Compressor, Visita técnica)" value={it.description} disabled={finished}
                      onChange={(e) => upd("description", e.target.value)} data-testid={`quote-item-desc-${i}`} />
                    <Select value={it.kind} disabled={finished} onValueChange={(v) => upd("kind", v)}>
                      <SelectTrigger className="col-span-3" data-testid={`quote-item-kind-${i}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="mao_de_obra">Mão de obra</SelectItem>
                        <SelectItem value="peca">Peça</SelectItem>
                        <SelectItem value="outro">Outro</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input className="col-span-1" type="number" min="1" step="1" value={it.quantity} disabled={finished}
                      onChange={(e) => upd("quantity", e.target.value)} data-testid={`quote-item-qty-${i}`} title="Qtd" />
                    <Input className="col-span-2" type="number" min="0" step="0.01" placeholder="Unit." value={it.unit_price} disabled={finished}
                      onChange={(e) => upd("unit_price", e.target.value)} data-testid={`quote-item-price-${i}`} title="Valor unitário" />
                    <button type="button" disabled={finished} className="col-span-1 text-slate-400 hover:text-red-500 flex justify-center"
                      onClick={() => setQuoteItems((arr) => arr.filter((_, idx) => idx !== i))} data-testid={`quote-item-remove-${i}`}>
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                );
              })}
              {!finished && (
                <div className="flex flex-wrap items-center gap-2">
                  <Button type="button" size="sm" variant="outline" className="gap-1.5 border-amber-300 text-amber-700 hover:bg-amber-100"
                    onClick={() => setQuoteItems((arr) => [...arr, { description: "", kind: "peca", quantity: 1, unit_price: 0 }])}
                    data-testid="quote-item-add">+ Adicionar item</Button>
                  {catalog.length > 0 && (
                    <Select value="" onValueChange={(id) => {
                      const s = catalog.find((c) => c.id === id);
                      if (s) setQuoteItems((arr) => [...arr, { description: s.name, kind: s.kind || "peca", quantity: 1, unit_price: s.value || 0 }]);
                    }}>
                      <SelectTrigger className="w-56 h-9" data-testid="quote-catalog-select"><SelectValue placeholder="+ Adicionar do catálogo" /></SelectTrigger>
                      <SelectContent>
                        {catalog.map((s) => (
                          <SelectItem key={s.id} value={s.id} data-testid={`quote-catalog-item-${s.id}`}>
                            {s.name} — {("R$ " + Number(s.value).toFixed(2)).replace(".", ",")}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
              )}
              <div className="flex justify-between items-center pt-1 border-t border-amber-200 text-sm font-semibold text-amber-900" data-testid="quote-items-total">
                <span>Total do orçamento</span>
                <span>{valueText || "R$ 0,00"}</span>
              </div>
            </div>
          )}

          <div className="rounded-lg border border-slate-200 p-3 space-y-2">
            <Label className="text-sm flex items-center gap-1.5"><DollarSign className="h-4 w-4 text-[#1e7be0]" /> Pagamento</Label>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1.5">
                <Label className="text-xs">Valor do serviço (R$)</Label>
                {isQuote ? (
                  <Input type="text" value={valueText || "R$ 0,00"} readOnly
                    className="bg-slate-50 font-semibold" data-testid="os-value-input"
                    title="Calculado automaticamente pelos itens do orçamento" />
                ) : (
                  <Input type="number" min="0" step="0.01" value={serviceValue} disabled={finished}
                    onChange={(e) => setServiceValue(e.target.value)} placeholder="0,00" data-testid="os-value-input" />
                )}
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Forma de pagamento</Label>
                <Select value={paymentMethod || "none"} disabled={finished} onValueChange={(v) => setPaymentMethod(v === "none" ? "" : v)}>
                  <SelectTrigger data-testid="os-payment-select"><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Não informado</SelectItem>
                    {PAYMENT_METHODS.map((m) => <SelectItem key={m} value={m} data-testid={`os-payment-${m}`}>{m}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Condições de pagamento</Label>
              <Input value={paymentConditions} disabled={finished} onChange={(e) => setPaymentConditions(e.target.value)}
                placeholder="Ex: À vista, 2x sem juros, 50% na entrada..." data-testid="os-payment-conditions-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Status do pagamento</Label>
              <div className="flex gap-2">
                <Button type="button" variant={paymentStatus === "pendente" ? "default" : "outline"} disabled={finished}
                  className={`flex-1 h-9 ${paymentStatus === "pendente" ? "bg-amber-500 hover:bg-amber-600" : ""}`}
                  onClick={() => setPaymentStatus("pendente")} data-testid="os-payment-status-pendente">Pendente</Button>
                <Button type="button" variant={paymentStatus === "pago" ? "default" : "outline"} disabled={finished}
                  className={`flex-1 h-9 ${paymentStatus === "pago" ? "bg-emerald-600 hover:bg-emerald-700" : ""}`}
                  onClick={() => setPaymentStatus("pago")} data-testid="os-payment-status-pago">Pago</Button>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Cupom de fidelidade do cliente</Label>
              {request.coupon_code ? (
                <p className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-2.5 py-2" data-testid="os-coupon-applied">
                  Cupom <b>{request.coupon_code}</b> aplicado — desconto de R$ {Number(request.coupon_discount || 0).toFixed(2).replace(".", ",")}
                </p>
              ) : (
                <Input value={couponInput} disabled={finished} onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                  placeholder="Ex: CB1A2B3C" data-testid="os-coupon-input" />
              )}
            </div>
            {isPix && (
              (pixKey || pixQr) ? (
                <div className="rounded-md bg-slate-50 border border-slate-200 p-2.5 flex items-center gap-3" data-testid="os-pix-info">
                  {pixQr && <img src={fileUrl(pixQr)} alt="QR Pix" className="h-24 w-24 object-contain rounded border border-slate-200 bg-white" data-testid="os-pix-qr" />}
                  <div className="text-xs text-slate-600 min-w-0">
                    <p className="flex items-center gap-1 font-medium text-slate-700"><QrCode className="h-3.5 w-3.5" /> Pix para o cliente</p>
                    {pixKey && <p className="break-all mt-1"><b>Chave:</b> {pixKey}</p>}
                    {pixOwner && <p><b>Recebedor:</b> {pixOwner}</p>}
                  </div>
                </div>
              ) : (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2.5 py-2" data-testid="os-pix-missing">
                  Nenhuma chave Pix configurada. Configure em "Meu Pix" (ícone de QR Code no topo).
                </p>
              )
            )}
          </div>

          <div className="rounded-lg border border-slate-200 p-3 space-y-2" data-testid="os-parts-section">
            <Label className="text-sm flex items-center gap-1.5"><Wrench className="h-4 w-4 text-[#1e7be0]" /> Peças utilizadas</Label>
            {partsUsed.map((p, i) => (
              <div key={i} className="flex gap-2 items-center">
                <Input value={p.name} disabled={finished} placeholder="Nome da peça"
                  onChange={(e) => setPartsUsed((arr) => arr.map((x, j) => j === i ? { ...x, name: e.target.value } : x))}
                  data-testid={`os-part-name-${i}`} />
                <Input type="number" min="1" value={p.quantity} disabled={finished} className="w-20"
                  onChange={(e) => setPartsUsed((arr) => arr.map((x, j) => j === i ? { ...x, quantity: e.target.value } : x))}
                  data-testid={`os-part-qty-${i}`} />
                {!finished && (
                  <Button type="button" variant="ghost" size="icon" className="h-9 w-9 text-red-500 flex-shrink-0"
                    onClick={() => setPartsUsed((arr) => arr.filter((_, j) => j !== i))} data-testid={`os-part-remove-${i}`}>
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            ))}
            {!finished && (
              <Button type="button" variant="outline" size="sm" className="gap-1.5"
                onClick={() => setPartsUsed((arr) => [...arr, { name: "", quantity: 1 }])} data-testid="os-part-add">
                <ImagePlus className="h-4 w-4" /> Adicionar peça
              </Button>
            )}
          </div>

          <div className="rounded-lg border border-emerald-200 bg-emerald-50/50 p-3 space-y-2" data-testid="laudo-section">
            <Label className="text-sm flex items-center gap-1.5 text-emerald-800"><Award className="h-4 w-4" /> Laudo / Certificado de Garantia</Label>
            <p className="text-xs text-emerald-700">
              Gere o laudo de conclusão por serviço autorizado, com data de conclusão, tempo de garantia e observações.
              {request.status !== "concluido" && " (Conclua o chamado para registrar a data de conclusão.)"}
            </p>
            <div className="grid grid-cols-2 gap-2">
              <Button variant="outline" className="gap-1.5 border-emerald-500 text-emerald-700 hover:bg-emerald-100"
                onClick={downloadLaudo} disabled={laudoBusy} data-testid="laudo-pdf-button">
                <Download className="h-4 w-4" /> {laudoBusy ? "Gerando..." : "Baixar Laudo"}
              </Button>
              <Button variant="outline" className="gap-1.5 border-emerald-500 text-emerald-700 hover:bg-emerald-100"
                onClick={sendLaudoEmail} disabled={laudoEmailing} data-testid="laudo-email-button">
                <Mail className="h-4 w-4" /> {laudoEmailing ? "Enviando..." : "Laudo por e-mail"}
              </Button>
            </div>
          </div>

          {canEditClosed && <ElginCertificateActions request={request} osNumber={osNumber} />}

          {finished ? (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2" data-testid="os-locked-note">
              Chamado {request.status === "concluido" ? "concluído" : "cancelado"} — a OS não pode mais ser editada. Você ainda pode imprimir ou enviar por WhatsApp.
            </p>
          ) : (
            <>
              {isClosed && (
                <p className="text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded-md px-3 py-2" data-testid="os-edit-closed-note">
                  Chamado {request.status === "concluido" ? "concluído" : "cancelado"} — edição liberada para gestor/franqueado.
                </p>
              )}
              <Button className="w-full bg-blue-600 hover:bg-blue-700 gap-1.5" onClick={() => save()} disabled={busy} data-testid="os-save-button">
                <Save className="h-4 w-4" /> {busy ? "Salvando..." : "Salvar ordem de serviço"}
              </Button>
              {canEditClosed && !isClosed && (
                <CloseRequestButton request={request} testPrefix="os" beforeClose={() => save(true)}
                  onChanged={() => { onSaved?.(); onOpenChange(false); }} />
              )}
            </>
          )}
        </div>

        <DialogFooter className="grid grid-cols-2 gap-2 sm:flex-row">
          <Button variant="outline" className="gap-1.5" onClick={printOS} data-testid="os-print-button">
            <Printer className="h-4 w-4" /> Imprimir OS
          </Button>
          <Button variant="outline" className="gap-1.5" onClick={downloadPdf} disabled={downloading} data-testid="os-pdf-button">
            <Download className="h-4 w-4" /> {downloading ? "Gerando..." : "Baixar PDF"}
          </Button>
          <Button variant="outline" className="gap-1.5 border-[#1e7be0] text-[#1e7be0] hover:bg-blue-50" onClick={sendEmail} disabled={emailing} data-testid="os-email-button">
            <Mail className="h-4 w-4" /> {emailing ? "Enviando..." : "Enviar por e-mail"}
          </Button>
          <Button variant="outline" className="gap-1.5 border-green-500 text-green-700 hover:bg-green-50" onClick={sendWhatsApp} data-testid="os-whatsapp-button">
            <MessageCircle className="h-4 w-4" /> Enviar WhatsApp
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <StaffEquipmentDialog open={showEquipReg} onOpenChange={setShowEquipReg}
      requestId={request.id}
      presetCustomer={{ name: request.customer_name, phone: request.customer_phone, address: request.service_address }}
      onSaved={(equip) => { onSaved?.(); if (equip) setQrEquip(equip); }} />
    <QrCodeDialog open={!!qrEquip} onOpenChange={(o) => !o && setQrEquip(null)}
      qrToken={qrEquip?.qr_token} equipment={qrEquip}
      equipmentLabel={qrEquip ? [qrEquip.equipment_type, qrEquip.brand, qrEquip.model].filter(Boolean).join(" ") : ""} />
    <AssignCustomerDialog open={showAssignCustomer} onOpenChange={setShowAssignCustomer}
      requestId={request.id} onLinked={() => onSaved?.()} />
    </>
  );
}
